/**
 * "Got your letter, replying properly soon" (2026-10-06).
 *
 * The recipient sends it on purpose, once per letter; opening a letter never
 * sends it. So the sender's view says only what the recipient chose to say —
 * and when there is nothing, it says nothing. No tick, no "seen", and no time
 * stamp that would turn a kindness into a "seen at".
 */

import { useState } from 'react';
import { Hourglass } from 'lucide-react';
import { ACKNOWLEDGEMENT_COPY, canAcknowledge } from '@toiyp/shared';
import { apiErrorMessage } from '../letters/ui';
import { useAcknowledgeMutation, type ApiLetter } from '../letters/api';

/** On a letter you received: the chip you tap to let them know. */
export function AcknowledgeChip({ letter, peerName }: { letter: ApiLetter; peerName: string }) {
  const [acknowledge, { isLoading }] = useAcknowledgeMutation();
  const [error, setError] = useState<string | null>(null);

  if (letter.acknowledgedAt) {
    if (letter.state === 'answered') return null;
    return (
      <p className="ack-note ack-note--sent" role="status">
        <Hourglass size={13} aria-hidden="true" />
        You let @{peerName} know a proper reply is coming.
      </p>
    );
  }
  if (!canAcknowledge(letter)) return null;

  return (
    <div className="ack-offer">
      <button
        type="button"
        className="ack-chip"
        disabled={isLoading}
        onClick={async () => {
          setError(null);
          try {
            await acknowledge({ id: letter.id }).unwrap();
          } catch (e) {
            setError(apiErrorMessage(e, 'Could not send that'));
          }
        }}
      >
        <Hourglass size={13} aria-hidden="true" />
        {isLoading ? 'Sending…' : ACKNOWLEDGEMENT_COPY}
      </button>
      <span className="ack-offer__hint">
        Not ready to write back yet? This tells @{peerName} it arrived safely. Once per letter.
      </span>
      {error && <span className="ack-offer__error" role="alert">{error}</span>}
    </div>
  );
}

/** On a letter you sent: their acknowledgement, gently, until the reply lands. */
export function AcknowledgedNote({ letter, peerName, compact = false }: { letter: ApiLetter; peerName: string; compact?: boolean }) {
  if (!letter.acknowledgedAt || letter.state === 'answered') return null;
  if (compact) {
    return (
      <span className="ack-note ack-note--compact">
        <Hourglass size={10} aria-hidden="true" /> @{peerName} is writing back properly
      </span>
    );
  }
  return (
    <p className="ack-note" role="status">
      <Hourglass size={13} aria-hidden="true" />
      <span>
        <strong>@{peerName}:</strong> “{ACKNOWLEDGEMENT_COPY}”
      </span>
    </p>
  );
}
